/** @jsx jsx */
import React from 'react'
import { jsx } from 'theme-ui'
import { Link } from 'gatsby'
import { Helmet } from 'react-helmet-async'
import useSiteMetadata from '@/hooks/use-site-metadata'

type BreadcrumbItem = {
  name: string
  url: string
}

type BreadcrumbsProps = {
  items: BreadcrumbItem[]
  background?: string
  textColor?: string
  separatorColor?: string
  ariaLabel?: string
}

const Breadcrumbs = ({
  items,
  background,
  textColor = '#0f172a',
  separatorColor = '#94a3b8',
  ariaLabel = 'Breadcrumb',
}: BreadcrumbsProps) => {
  if (!items || items.length === 0) return null

  const { siteUrl } = useSiteMetadata()
  const toAbsolute = (url: string) =>
    url.startsWith('http') ? url : `${siteUrl}${url}`

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: toAbsolute(item.url),
    })),
  }

  return (
    <div sx={{ width: '100%', backgroundColor: background }}>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <nav aria-label={ariaLabel} sx={{ width: '100%' }}>
        <ol
          sx={{
            listStyle: 'none',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            padding: ['8px 16px', '10px 24px', '12px 32px'],
            margin: 0,
            maxWidth: '1120px',
            color: textColor,
            fontSize: ['13px', '14px', '15px'],
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          {items.map((item, index) => {
            const isLast = index === items.length - 1
            return (
              <li
                key={item.url}
                sx={{ display: 'inline-flex', alignItems: 'center' }}
              >
                {isLast ? (
                  <span
                    aria-current="page"
                    sx={{ color: textColor, fontWeight: 700 }}
                  >
                    {item.name}
                  </span>
                ) : (
                  <Link
                    to={item.url}
                    sx={{
                      color: textColor,
                      textDecoration: 'none',
                      ':hover': { textDecoration: 'underline' },
                    }}
                  >
                    {item.name}
                  </Link>
                )}
                {!isLast && (
                  <span sx={{ margin: '0 8px', color: separatorColor }}>/</span>
                )}
              </li>
            )
          })}
        </ol>
      </nav>
    </div>
  )
}

export type { BreadcrumbItem, BreadcrumbsProps }
export default Breadcrumbs
